import { TinyDropdownMenuTrigger } from "$root/components/DropdownMenuTrigger";
import { ClickableArea, Controller, Icon, Text } from "@mdotm/shadcn/legacy/components";
import { TextArea, DropdownMenu, IconButton, Button } from "@mdotm/shadcn/mdotm/components";
import { toClassName } from "@mdotm/shadcn/legacy/react-extensions";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { noop, unpromisify } from "@mdotm/shadcn/legacy/utils";
import { useAsync, type MaybePromise } from "@mdotm/utils/promise";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FavouriteIcon } from "../icons/FavouriteIcon";

export type HistoryBoxProps = {
	message: string;
	favourite?: boolean;
	/** This conversation is the one currently open in the Chat tab — the row stays highlighted. */
	active?: boolean;
	onToggleFavourite(): MaybePromise<void>;
	/** Reopen the conversation this question started, restored as it was left. */
	onOpen(): void;
	onDelete(): MaybePromise<void>;
	onRename(newName: string): MaybePromise<void>;
};

/**
 * One question row in the History tab. The question text is the primary element (left, single
 * line, ellipsis-truncated) and clicking it REOPENS that conversation; the star (favourite toggle,
 * always visible) and the kebab (rare Rename / Delete actions) sit at the right. An
 * arrow fades in at the end of the text lane on hover to signal the row is actionable.
 */
export function HistoryBox({
	message,
	favourite,
	active,
	onDelete,
	onRename,
	onOpen,
	onToggleFavourite,
}: HistoryBoxProps): React.JSX.Element {
	const { t } = useTranslation();
	const [isEdit, setIsEdit] = useState(false);
	const selection: "favourite" | "default" = useMemo(() => (favourite ? "favourite" : "default"), [favourite]);
	// Star: filled/colored when favourite, outline-only otherwise.
	const starColor: Record<"favourite" | "default", { color: string; stroke: string }> = {
		favourite: { color: themeCSSVars.palette_B500, stroke: themeCSSVars.palette_B500 },
		default: { color: "transparent", stroke: themeCSSVars.palette_N300 },
	};

	const [doRename, renameState] = useAsync<void, [string]>(async (newName) => {
		await onRename(newName);
	});
	const [doToggleFavourite, toggleFavouriteState] = useAsync(async () => {
		await onToggleFavourite();
	});

	if (isEdit) {
		return (
			// Same lane, rhythm and type size as the read row: the field sits exactly where the question
			// text sits and the confirm/cancel controls take the star/kebab's place. Rename used to render
			// in a tinted box with a 16px extra inset and 16px type (vs the row's 12px), so opening it made
			// the row jump sideways and change size. `size="x-small"` is the 12px field step.
			<div className="flex w-full items-center gap-1 rounded px-2 py-1">
				<Controller value={message}>
					{({ value, onChange }) => (
						<>
							<div className="min-w-0 flex-1">
								<TextArea
									size="x-small"
									disabled={renameState.kind === "loading"}
									rows={1}
									className="w-full! [&_textarea]:resize-none!"
									value={value}
									onChangeText={onChange}
									onKeyDown={(e) => {
										// Escape gets you out — the row is a one-line rename, not a text editor, so
										// there's no reason to force a trip to the ✕.
										if (e.key === "Escape") {
											setIsEdit(false);
											return;
										}
										if (e.key === "Enter" && !e.shiftKey) {
											e.preventDefault();
											e.stopPropagation();
											doRename(value).then(() => setIsEdit(false), noop);
										}
									}}
								/>
							</div>
							{/* Neutral action first, primary last — same order as the platform's dialog footer. */}
							<IconButton
								unstyled
								disabled={renameState.kind === "loading"}
								type="button"
								title={t("CANCEL")}
								className="shrink-0 p-1"
								onClick={() => setIsEdit(false)}
								icon="Close"
								size={16}
								color={themeCSSVars.palette_N400}
							/>
							<IconButton
								unstyled
								disabled={renameState.kind === "loading"}
								type="button"
								title={t("CONFIRM")}
								className="shrink-0 p-1"
								onClick={unpromisify(async () => {
									await doRename(value);
									setIsEdit(false);
								})}
								icon="Icon-full-ok"
								size={16}
								color={themeCSSVars.palette_P500}
							/>
						</>
					)}
				</Controller>
			</div>
		);
	}

	return (
		<div
			// The active row keeps the hover highlight permanently, so "where am I" survives moving the
			// mouse away; `aria-current` says the same thing to assistive tech.
			aria-current={active ? "true" : undefined}
			className={toClassName({
				"group flex w-full items-center gap-1 rounded px-2 py-2 transition-colors": true,
				"bg-[var(--palette-N100)]": active === true,
				"hover:bg-[var(--palette-N100)]": active !== true,
			})}
		>
			{/* Primary: the question text. Clicking reopens the conversation it started. `ClickableArea`
			    styles an inner div via `className` and the outer flex-item via `wrapperAppearance`, so
			    the grow/min-width must go on the wrapper for the text lane to fill the row. */}
			<ClickableArea
				onClick={() => onOpen()}
				wrapperAppearance={{ className: "min-w-0 flex-1" }}
				className="flex min-w-0 items-center gap-1.5"
			>
				<Text
					as="span"
					// Medium weight on the open conversation, so the active row reads even at a glance.
					type={active ? "Body/M/Medium" : "Body/M/Book"}
					color={themeCSSVars.palette_N700}
					className="min-w-0 flex-1 truncate"
				>
					{message}
				</Text>
				{/* Open affordance — fades/slides in on row hover. */}
				<OpenArrow
					title={t("sphereAi.history.open")}
					className="text-[var(--palette-N400)] shrink-0 -translate-x-1 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100"
				/>
			</ClickableArea>

			{/* Star — always visible; frequent action, kept out of the kebab. */}
			<Button
				disabled={toggleFavouriteState.kind === "loading"}
				unstyled
				type="button"
				onClick={unpromisify(() => doToggleFavourite())}
				title={t("sphereAi.history.favourites")}
				className="shrink-0 p-1"
			>
				<FavouriteIcon
					color={starColor[selection].color}
					stroke={starColor[selection].stroke}
					style={{ width: "16px" }}
				/>
			</Button>

			{/* Kebab — rare actions. */}
			<DropdownMenu
				Trigger={TinyDropdownMenuTrigger}
				extraTriggerProps={{
					children: <Icon icon="More" className="rotate-90" size={20} color={themeCSSVars.palette_N400} />,
				}}
				actions={[
					{ icon: "Edit", onClick: () => setIsEdit(true), label: t("sphereAi.history.rename") },
					{ icon: "Delete", onClickAsync: async () => await onDelete(), label: t("sphereAi.history.delete") },
				]}
			/>
		</div>
	);
}

/** Small right chevron (currentColor) — the icon set has no plain chevron glyph. */
function OpenArrow({ className, title }: { className?: string; title: string }): React.JSX.Element {
	return (
		<svg viewBox="0 0 24 24" fill="none" className={className} width="14" height="14" role="img">
			<title>{title}</title>
			<path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
		</svg>
	);
}
